import { useFormik } from "formik";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import useGigsSelectorByPagination from "../../store/hooks/useGigsSelectorByPagination";
import { gigsByPaginate } from "../../store/thunks/gigsThunk";
import { colors } from "../../utils/Colors";
import GigPlaceholder from "../common/Placeholders/GigPlaceholder";
import FilterDropdowns from "../Gigs/FilterDropdowns";
import GigCard from "../Gigs/GigCard";

const GigsContainer = ({ mapGigs = [], gigsLoading: loading }) => {
  const scrollViewRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams({
    "gigs.location": "areaWise",
  });
  const [visibleGigs, setVisibleGigs] = useState([]);
  const [gigsToShow, setGigsToShow] = useState(mapGigs?.slice(0,10)||[]);

  // Read AI-applied search text from Redux
  const aiSearchText = useSelector((state) => state.aiSearch?.filters?.searchText || "");

  useEffect(() => {
    setGigsToShow(mapGigs?.slice(0,10)||[]);
    setVisibleGigs(10); // Reset visible count on update
  }, [mapGigs]);

  useEffect(() => {
    setGigsToShow(mapGigs?.slice(0,visibleGigs))
  }, [visibleGigs]);

  const dispatch = useDispatch();

  const {
    data: gigs,
    loading: gigsLoading,
    hasMore,
    page,
    limit,
  } = useGigsSelectorByPagination();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 800);

  const {
    handleChange,
    handleSubmit,
    values,
    setFieldValue,
    errors,
    resetForm,
  } = useFormik({
    initialValues: useMemo(() => {
      return {
        category: searchParams.get("gigs.category") || "",
        searchText: searchParams.get("gigs.searchText") || aiSearchText || "",
        sortBy: searchParams.get("gigs.sortBy") || "",
        level: searchParams.get("gigs.level") || "",
        minPrice: searchParams.get("gigs.minPrice") || "",
        maxPrice: searchParams.get("gigs.maxPrice") || "",
        subcategory: searchParams.get("gigs.subcategory") || "",
        userId: searchParams.get("gigs.userId") || "",
        location: searchParams.get("gigs.location") || "areaWise",
      };
    }, [searchParams]), // eslint-disable-line
    onSubmit: (values) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("gigs.searchText", values.searchText);
        next.set("gigs.sortBy", values.sortBy);
        next.set("gigs.level", values.level);
        next.set("gigs.minPrice", values.minPrice);
        next.set("gigs.maxPrice", values.maxPrice);
        next.set("gigs.location", values.location);
        return next;
      });
    },
  });

  // Sync AI search text into the Formik input whenever Redux updates it
  useEffect(() => {
    if (aiSearchText && aiSearchText !== values.searchText) {
      setFieldValue("searchText", aiSearchText);
    }
  }, [aiSearchText]); // eslint-disable-line

  // Add effect to handle filter changes
  useEffect(() => {
    const filters = {
      searchText: searchParams.get("gigs.searchText") || aiSearchText || "",
      sortBy: searchParams.get("gigs.sortBy") || "",
      level: searchParams.get("gigs.level") || "",
      minPrice: searchParams.get("gigs.minPrice") || "",
      maxPrice: searchParams.get("gigs.maxPrice") || "",
    };

    // Reset pagination and fetch first page when filters change
    dispatch(gigsByPaginate({ ...filters, page: 1 }));
  }, [dispatch, searchParams, aiSearchText]); // eslint-disable-line

  const handleScroll = useCallback(() => {
    if (!hasMore || searchParams.get("gigs.location") !== "global") {
      if (scrollViewRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = scrollViewRef.current;
        const threshold = scrollHeight * 0.9;
        if (scrollTop + clientHeight >= threshold && !loading) {
          setVisibleGigs((prev) => prev + 10);
        }
      }
      return;
    }

    const gigUrlFilters = {
      searchText: searchParams.get("gigs.searchText") || "",
      sortBy: searchParams.get("gigs.sortBy") || "",
      level: searchParams.get("gigs.level") || "",
      minPrice: searchParams.get("gigs.minPrice") || "",
      maxPrice: searchParams.get("gigs.maxPrice") || "",
    };

    if (isMobile) {
      const threshold = document.body.offsetHeight * 0.8;
      if (window.innerHeight + window.scrollY >= threshold && !loading) {
        dispatch(gigsByPaginate({ ...gigUrlFilters, page: page + 1 }));
      }
    } else {
      if (scrollViewRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = scrollViewRef.current;
        const threshold = scrollHeight * 0.9;
        if (scrollTop + clientHeight >= threshold && !loading) {
          dispatch(gigsByPaginate({ ...gigUrlFilters, page: page + 1 }));
        }
      }
    }
  }, [hasMore, searchParams, isMobile, loading, dispatch, page]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 800);
    };

    // Run the layout reads in handleScroll at most once per frame.
    let raf = null;
    const onScroll = () => {
      if (raf != null) return;
      raf = window.requestAnimationFrame(() => {
        raf = null;
        handleScroll();
      });
    };

    const scrollEl = scrollViewRef.current;
    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", onScroll, { passive: true });

    if (scrollEl) {
      scrollEl.addEventListener("scroll", onScroll, { passive: true });
    }

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", onScroll);
      if (raf != null) window.cancelAnimationFrame(raf);

      if (scrollEl) {
        scrollEl.removeEventListener("scroll", onScroll);
      }
    };
  }, [isMobile, handleScroll]);

  return (
    <div className="list" ref={scrollViewRef}>
      <div className="home-dropdown">
        <div className="list-header">
          <h2 className="gigs-title">
            Find The{" "}
            <span>
              Services
              <br />
            </span>{" "}
            You Need
          </h2>
        </div>
        <FilterDropdowns
          handleChange={handleChange}
          handleSubmit={handleSubmit}
          values={values}
          setFieldValue={setFieldValue}
          errors={errors}
          resetForm={resetForm}
          placeholder="Search by service title or username"
        />
      </div>
      <div className="d-flex flex-wrap justify-content-center gap-2">
        {(searchParams.get("gigs.location") || "areaWise") === "areaWise" ? (
          (() => {
            const allGigs = [...(mapGigs || [])];
            return allGigs.length > 0 ? (
              gigsToShow.map((gig) => <GigCard key={gig._id} {...gig} />)
            ) : loading ? (
              <GigPlaceholder placeholders={[1, 2, 3, 4, 5, 6]} />
            ) : (
              <div
                className="d-flex align-items-center justify-content-center mt-5"
                style={{ color: colors.color.darkGrey }}
              >
                <p>Sorry ... No Gigs Found</p>
              </div>
            );
          })()
        ) : (
          <>
            {!!gigs?.length &&
              gigs?.map((gig, index) => <GigCard key={index} {...gig} />)}
            {gigsLoading ? (
              <GigPlaceholder placeholders={[1, 2, 3, 4, 5, 6]} />
            ) : (
              !gigsLoading &&
              !gigs?.length && (
                <div
                  className="d-flex align-items-center justify-content-center mt-5"
                  style={{ color: colors.color.darkGrey }}
                >
                  <p>Sorry ... No Gig Found</p>
                </div>
              )
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default GigsContainer;
